/**
 * The NokNok brand, taken from the logo rather than from memory.
 *
 * `public/images/icon/nn-logo.png` is two colours and nothing else: a petrol
 * field and a yellow wordmark. Sampling the file gives #013042 across 274,814
 * opaque pixels and #ffd600 across 24,344 — every other value in it is edge
 * antialiasing. Those two hexes are therefore the whole palette, and everything
 * below is derived from them rather than picked alongside them.
 *
 * Both ramps hold one hue and move only saturation and lightness:
 *
 *   petrol  hsl(197, .., ..)   #013042 is the 900
 *   yellow  hsl( 50, 100%, ..) #ffd600 is the 500
 *
 * The steps are named the way Tailwind and Radix name theirs, so "600 on white"
 * and "300 on the dark field" are the two readings that matter and both are
 * recorded in the comments. Contrast figures are WCAG ratios, measured, not
 * estimated.
 */

/** The two literal logo colours. Nothing else may claim to be "the brand colour". */
export const BRAND_PETROL: string = '#013042';
export const BRAND_YELLOW: string = '#ffd600';

/**
 * Petrol. The structural colour: chrome, links, active state, focus, borders.
 *
 * The middle of the ramp is where the work happens. 600 is the lightest step
 * that clears 4.5:1 on white (6.39:1), so it is the light theme's interactive
 * colour; 300 clears 7:1 on both dark surfaces (8.49 and 7.44), so it is the
 * dark theme's. 900 is the logo itself and is only ever a background.
 */
export const petrol = {
    50: '#f0f7f9',
    100: '#d8ebf3',
    200: '#acd6e7',
    300: '#73b8d3',
    400: '#319ac4',
    500: '#127ca5',
    600: '#09668b',
    700: '#044e6c',
    800: '#023d55',
    900: BRAND_PETROL,
    950: '#021d27',
} as const;

/**
 * Yellow. Reserved: it means live — selection, execution, the one primary
 * action on a screen. This is the rule the workflow canvas already runs on
 * (`workflow/styles/_tokens.scss`) and the admin now runs on the same one, so
 * a yellow thing anywhere in HQ is a thing that is happening or chosen.
 *
 * It has no contrast on white as a stroke (1.43:1), which is why fills and
 * lines are separate steps: 500 fills, 700 draws, 800 sets text.
 */
export const yellow = {
    200: '#ffee99',
    300: '#ffe45c',
    400: '#ffdb29',
    500: BRAND_YELLOW,
    600: '#d6b200',
    700: '#ad9000',
    800: '#8a7300',
} as const;

/**
 * Status. Petrol and yellow cannot carry success and failure, so these are the
 * only hues in the system that are not derived from the logo. They are the same
 * values the canvas uses, so a failed node and a failed list row are one colour.
 */
export const status = {
    okLight: '#15803d',
    okDark: '#4ade80',
    badLight: '#dc2626',
    badDark: '#fb6f6f',
    warnLight: '#b45309',
    warnDark: '#fbbf24',
} as const;

/**
 * Surfaces, kept in step with the `--wf-*` custom properties by hand. The two
 * layers have to agree because they meet on the same screen: a workflow canvas
 * sits inside an MUI page, and a node dialog is an MUI dialog wearing canvas
 * styles.
 */
export const surface = {
    lightField: '#eef4f6',
    lightPaper: '#ffffff',
    lightRaised: '#f6fafb',
    lightInk: '#06222e',
    lightInkDim: '#45646f',
    lightLine: 'rgba(1, 48, 66, 0.13)',
    lightLineStrong: 'rgba(1, 48, 66, 0.27)',

    darkField: '#061419',
    darkPaper: '#0d222b',
    darkRaised: '#12303b',
    darkInk: '#e6f1f5',
    darkInkDim: '#8fb0bd',
    darkLine: 'rgba(126, 178, 196, 0.16)',
    darkLineStrong: 'rgba(126, 178, 196, 0.30)',
} as const;

/**
 * MUI wants twenty-five elevations and uses maybe six of them. Rather than
 * writing out an array of neutral greys, both themes tint their shadows with
 * the brand petrol, which is what stops a raised card reading as a grey box
 * dropped onto a coloured page.
 *
 * @param tint rgb triple the shadow is built from, as a bare "r, g, b" string.
 * @param scale multiplies every alpha; dark surfaces need a heavier shadow to
 *              register at all.
 */
export const tintedShadows: (tint: string, scale: number) => string[] =
    (tint: string, scale: number): string[] => {
        const a = (value: number): string => (value * scale).toFixed(3);

        return Array.from({ length: 25 }, (_unused: unknown, level: number): string => {
            if (level === 0) {
                return 'none';
            }

            // Two shadows per level: a tight contact shadow that never grows much,
            // and a wide ambient one that carries the height.
            const spread: number = Math.round(level * 1.6) + 2;
            const drop: number = Math.round(level * 0.9) + 1;

            return [
                `0 ${Math.max(1, Math.round(level / 4))}px ${Math.max(2, Math.round(level / 2))}px rgba(${tint}, ${a(0.08)})`,
                `0 ${drop}px ${spread}px -${Math.round(spread / 2)}px rgba(${tint}, ${a(0.18)})`,
            ].join(', ');
        });
    };
